import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { LabelColor, LabelView } from '@vwt/contracts';
import { Button, cn, Dialog, DialogContent, Input, toast } from '@vwt/ui';
import { useApiMutation, useApiQuery } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { useTranslation } from '../lib/i18n.js';

/** Palette colors chosen to read on light and dark surfaces. */
export const LABEL_COLORS: Record<LabelColor, string> = {
  gray: '#8b929e',
  red: '#ef4444',
  orange: '#f97316',
  amber: '#eab308',
  green: '#22c55e',
  teal: '#14b8a6',
  blue: '#3b82f6',
  indigo: '#6366f1',
  purple: '#a855f7',
  pink: '#ec4899',
};
const PALETTE = Object.keys(LABEL_COLORS) as LabelColor[];

/** Label definitions keyed by normalized name; refreshed rarely (the catalog changes seldom). */
export function useLabelCatalog(): Map<string, LabelView> {
  const query = useApiQuery('listLabels', {}, { staleTime: 5 * 60_000 });
  return new Map((query.data?.data ?? []).map((l) => [l.name, l]));
}

/** A label chip with its catalog color (gray when the label has no definition). */
export function LabelBadge({ label, catalog, children, className }: { label: string; catalog: Map<string, LabelView>; children?: React.ReactNode; className?: string }) {
  const definition = catalog.get(label.trim().toLowerCase());
  const color = LABEL_COLORS[definition?.color ?? 'gray'];
  return (
    <span title={definition?.description ?? undefined} className={cn('inline-flex max-w-40 items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs font-medium whitespace-nowrap text-fg-muted', className)}>
      <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      <span className="truncate">{label}</span>
      {children}
    </span>
  );
}

function ColorPicker({ value, onChange, label }: { value: LabelColor; onChange: (color: LabelColor) => void; label: string }) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={t(`labels.colors.${c}`)}
          title={t(`labels.colors.${c}`)}
          onClick={() => onChange(c)}
          className={cn('size-5 rounded-full border-2', value === c ? 'border-fg' : 'border-transparent')}
          style={{ backgroundColor: LABEL_COLORS[c] }}
        />
      ))}
    </div>
  );
}

/** Catalog editor: color and description per label; deleting a definition keeps the label on items. */
export function LabelManagerDialog({ onClose, suggestions = [] }: { onClose: () => void; suggestions?: string[] }) {
  const { t } = useTranslation();
  const labels = useApiQuery('listLabels', {});
  const save = useApiMutation('saveLabel', { invalidate: ['listLabels'] });
  const remove = useApiMutation('deleteLabel', { invalidate: ['listLabels'] });
  const [name, setName] = useState('');
  const [color, setColor] = useState<LabelColor>('blue');
  const defined = labels.data?.data ?? [];
  const undefinedSuggestions = suggestions.filter((s) => !defined.some((d) => d.name === s.toLowerCase())).slice(0, 12);
  const upsert = async (body: { name: string; color: LabelColor; description?: string | null }) => {
    try {
      await save.mutateAsync({ body });
      toast.success(t('labels.saved'));
      return true;
    } catch (error) {
      toastError(error);
      return false;
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t('labels.title')} description={t('labels.hint')} closeLabel={t('common.close')} className="w-[min(560px,calc(100vw-2rem))]">
        <form
          className="flex flex-wrap items-end gap-2 border-b border-border pb-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (name.trim() && (await upsert({ name: name.trim(), color }))) setName('');
          }}
        >
          <label className="flex min-w-40 flex-1 flex-col gap-1 text-xs font-medium text-fg-muted">
            {t('labels.name')}
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} list="label-suggestions" />
            <datalist id="label-suggestions">
              {undefinedSuggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>
          <ColorPicker value={color} onChange={setColor} label={t('labels.color')} />
          <Button type="submit" variant="primary" size="sm" disabled={!name.trim()} loading={save.isPending}>
            {t('labels.add')}
          </Button>
        </form>
        {defined.length ? (
          <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {defined.map((l) => (
              <li key={l.id} className="flex flex-col gap-1.5 rounded-[var(--radius)] border border-border px-3 py-2">
                <div className="flex items-center gap-2">
                  <LabelBadge label={l.name} catalog={new Map([[l.name, l]])} />
                  <div className="ml-auto">
                    <ColorPicker value={l.color} onChange={(c) => void upsert({ name: l.name, color: c })} label={t('labels.color')} />
                  </div>
                  <Button variant="ghost" size="iconSm" aria-label={t('labels.delete', { name: l.name })} title={t('labels.delete', { name: l.name })} onClick={() => void remove.mutateAsync({ params: { id: l.id } }).catch(toastError)}>
                    <Trash2 />
                  </Button>
                </div>
                <Input
                  className="h-7"
                  defaultValue={l.description ?? ''}
                  maxLength={200}
                  placeholder={t('labels.description')}
                  aria-label={t('labels.description')}
                  onBlur={(e) => e.target.value !== (l.description ?? '') && void upsert({ name: l.name, color: l.color, description: e.target.value || null })}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-fg-subtle">{t('labels.empty')}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
